<template>
  <div class="historial-acuerdo-comercial" v-if="acuerdo_comercial.historial_eventos">

    <!-- Recorro el historial de eventos desde el final hasta el inicio (para mostrar los eventos en orden de más reciente a más antiguo) -->
    <div v-for="(evento, indice) in [...acuerdo_comercial.historial_eventos].reverse()" :key="indice" class="d-flex align-start flex-column">

      <!-- Icono evento + titulo evento + chip evento (en caso de ser el último evento) -->
      <div class="d-flex align-center">

        <!-- Icono del evento -->
        <div
          class="material-symbols-outlined icono icono--terminador"
          :class="{'icono--relleno': !esIconoTerminador(indice)}"
        >
          {{ esIconoTerminador(indice)  ? 'adjust' : 'fiber_manual_record' }}
        </div>

        <!-- Label del evento -->
        <div
          class="d-flex align-center mr-6 evento"
          :class="{'label-oscuro': indice === 0}">
          {{ textoLabelEvento(evento.evento) }}
        </div>

        <!-- Chip del evento -->
        <div v-if="indice === 0" class="d-flex align-center">
          <chip-estado-acuerdo-comercial :estado="estadoEventoActual()" />

          <!-- Caso borde: si el AC esta expirado mostramos el chip asociado -->
          <chip-estado-acuerdo-comercial v-if="acuerdo_comercial.expirada" :estado="ESTADO_ACUERDO_COMERCIAL_EXPIRADO" class="ml-2" />
        </div>
      </div>

      <!-- Contenido del evento -->
      <div
        class="contenido-evento evento evento--label-intermedio"
        :class="{'sin-borde-separador': indice === acuerdo_comercial.historial_eventos.length - 1}"
      >

        <!-- Evento expirado -->
        <div v-if="evento.evento === ESTADO_ACUERDO_COMERCIAL_EXPIRADO">
          <span v-if="evento.fecha">Expiración: {{ evento.fecha | formatFecha('dd/MM/yyyy') }}</span>
        </div>

        <!-- Si el evento no es expirado -->
        <div v-else>

          <!-- Fecha de evento -->
          <div class="mb-1">{{ evento.fecha | formatFecha('dd/MM/yyyy HH:mm') }} hs</div>

          <!-- Si el evento es no terminador -->
          <div v-if="!esUnEventoTerminador(evento.evento)">

            <!-- Label Usuario/Agente -->
            <span class="label-oscuro">{{ mostrarLabelUsuario(evento.evento) ? 'Usuario: ' : 'Agente: ' }}</span>

            <!-- Nombre + mail (Usuario) -->
            <span v-if="mostrarLabelUsuario(evento.evento)">{{ evento.usuario.nombre + ' (' + evento.usuario.mail + ')' }}</span>

            <!-- Nombre sales (Agente)-->
            <span v-else>{{ acuerdo_comercial.usuario_sales | trimUsuarioSales }}</span>

            <!-- Label cantidad de días agregados -->
            <div class="contenedor-label-dias-agregados" v-if="evento.evento === EVENTO_EXTENDIDO">
              <span class="label-oscuro">Cantidad de días agregados: </span>
              <span>{{ evento.dias_extendidos }}</span>
            </div>

            <!-- Mensaje con el motivo del rechazo -->
            <div class="mt-3" v-if="evento.rechazo_detalles">
              <span class="label-oscuro">Mensaje: </span>
              <span>{{ evento.rechazo_detalles }}</span>
            </div>
          </div>

        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { AcuerdoComercial } from '@/models/AcuerdoComercial';
import { TipoAcuerdo, TiposEstadosAcuerdoComercial } from '../../acuerdo-comercial.consts';
import ChipEstadoAcuerdoComercial from '../ChipEstadoAcuerdoComercial.vue';

/**
 * Vista encargada de mostrar el historial de un acuerdo comercial
 * 
 * Para mostrar el historial de eventos del acuerdo comercial, se recorre el historial de eventos y se los muestra en el orden del más reciente al más antiguo.
 * 
 * La vista tiene dos partes grandes:
 *  Primera parte:
 *    - Icono del evento (terminador / relleno, se condiciona en base al estado del evento)
 *    - Texto que describe el evento
 *    - Chip con el estado del acuerdo comercial (se muestra en caso de ser el último evento)
 *
 *  Segunda parte (contenido del evento):
 *    - Se separa entre evento expirado y no expirado (caso borde)
 *    - Para el caso de los no expirados siempre se muestra fecha y hora del evento primero
 *    - Luego si es un evento no terminador se muestran los labels asociados a cada evento en particular
 * 
 * Hay un caso especial:
 *  - Administración (para este caso los wordings de los labels son diferente a los otros)
 * 
 */
export default {
  name: 'VistaHistorialAcuerdoComercial',
  components: {
    ChipEstadoAcuerdoComercial
  },
  props: {
    acuerdo_comercial: { type: AcuerdoComercial, default: null }
  },
  data: () => ({
    MOSTRAR_LABEL_USUARIO: [TiposEstadosAcuerdoComercial.ACEPTADA, TiposEstadosAcuerdoComercial.RECHAZADA], // Eventos en los que se muestra el label del usuario

    // Eventos
    EVENTO_TERMINADOR: [TiposEstadosAcuerdoComercial.FACTURANDO, TiposEstadosAcuerdoComercial.ACTIVACION], // Eventos que son terminadores para el historial
    EVENTO_EXTENDIDO: TiposEstadosAcuerdoComercial.EXTENDIDO,

    // Estados del acuerdo comercial
    ESTADO_ACUERDO_COMERCIAL_EXPIRADO: TiposEstadosAcuerdoComercial.EXPIRADO
  }),
  methods: {

    /**
     * Retorna verdadero en caso de que se deba mostrar el icono terminal, falso en caso contrario
     * Caso verdadero: Si estamos en el primer evento o si estamos en el último evento del historial y el evento es facturando
     * Caso falso: En el resto de los casos (serian eventos intermedios)
     */
    esIconoTerminador(indice) {
      return indice === this.acuerdo_comercial.historial_eventos.length - 1 || (indice === 0 && this.acuerdo_comercial.estado === TiposEstadosAcuerdoComercial.FACTURANDO);
    },

    /**
     *  Retorna el estado actual del acuerdo comercial
     *  Caso borde: si el tipo del acuerdo es a la administración retornamos el estado de administración para mostrar el chip correcto
     */
    estadoEventoActual() {
      return this.acuerdo_comercial.tipo_propuesta === TipoAcuerdo.ADMINISTRACION ? TiposEstadosAcuerdoComercial.ADMINISTRACION : this.acuerdo_comercial.estado;
    },

    /**
     * Retorna verdadero en caso de que el evento pasado por parametro sea un evento terminador, falso en caso contrario
     */
    esUnEventoTerminador(evento) {
      return this.EVENTO_TERMINADOR.includes(evento);
    },

    /**
     * Retorna verdadero en caso de que el evento pasado por parametro este dentro de los eventos que hay que mostrar el label de usuario, falso en caso contrario
     */
    mostrarLabelUsuario(evento) {
      return this.MOSTRAR_LABEL_USUARIO.includes(evento);
    },

    /**
     * Retorna el texto del evento formateado en base al evento del acuerdo comercial
     */
    textoLabelEvento(evento) {
      switch (evento) {
      case TiposEstadosAcuerdoComercial.EXPIRADO:
        return 'Se venció el período de aceptación';
      case TiposEstadosAcuerdoComercial.PERIODO_ACEPTACION_FINALIZADO_POR_AGENTE:
        return 'Período de aceptación finalizado por agente';
      case TiposEstadosAcuerdoComercial.EXTENDIDO:
        return 'Se extendió el período de aceptación';
      case TiposEstadosAcuerdoComercial.FACTURANDO:
        return 'Inicio de facturación';
      case TiposEstadosAcuerdoComercial.ACTIVACION:
        return this.acuerdo_comercial.tipo_propuesta === TiposEstadosAcuerdoComercial.ADMINISTRACION
          ? 'Alta de administración' : 'Activación';
      case TiposEstadosAcuerdoComercial.NUEVA:
        return this.acuerdo_comercial.tipo_propuesta === TiposEstadosAcuerdoComercial.ADMINISTRACION
          ? 'Creación de acuerdo' : 'Acuerdo enviado';
      case TiposEstadosAcuerdoComercial.ACEPTADA:
        return 'Acuerdo aceptado';
      case TiposEstadosAcuerdoComercial.ACTUALIZADA:
        return this.acuerdo_comercial.tipo_propuesta === TiposEstadosAcuerdoComercial.ADMINISTRACION
          ? 'Modificación de acuerdo'
          : 'Acuerdo actualizado';
      case TiposEstadosAcuerdoComercial.RENEGOCIADA:
        return 'Acuerdo renegociado';
      case TiposEstadosAcuerdoComercial.RECHAZADA:
        return 'Acuerdo con solicitud de contacto';
      }
    }
  }
};
</script>

<style lang="scss">
.historial-acuerdo-comercial {

  .label-oscuro {
    font-weight: 700;
  }

  .v-chip {
    margin: 0;
  }

  .contenido-evento {
    padding: 4px 16px 20px 15px;
    border-left: 2px solid color(celeste-claro);
    margin-top: 4px;
    margin-bottom: 4px;
    margin-left: 11px;
  }

  .sin-borde-separador {
    border-left: none;
  }

  .contenedor-label-dias-agregados {
    margin-top: 10px;
  }

  .evento {
    color: color(dark);
    font-size: 14px;
    line-height: 18px;

    &--label-intermedio {
      color: color(gris-oscuro);
      font-size: 13px;
      line-height: 150%;
    }
  }

  .icono {
    margin-right: 6px;
    pointer-events: none;
    color: color(dark-secund);

    &--terminador {
      font-size: 24px;
      font-variation-settings:
        'FILL' 0,
        'wght' 400,
        'GRAD' 0,
        'opsz' 24
    }

    &--relleno {
      font-size: 20px;
      padding-left: 2px;
      font-variation-settings:
        'FILL' 1,
        'wght' 400,
        'GRAD' 0,
        'opsz' 24
    }
  }

}
</style>

